import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components-next';
import { PaloDetailSlot, PaloField, PaloSegmentedToggle } from '@synergy/components-palo';
import { useFormikContext } from 'formik';

import { OverviewFormValues, overviewFieldNames } from '../../../models/overview-form-values';
import { PaloFormikField } from '../../../../../../common/components/palo/palo-formik-field';
import { PaloSendOption } from '../../../../../../common/components/palo/palo-send-option';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import { toggleIsCredit } from '../../../store/invoice-detail-actions';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

interface PaloInvoiceHeadProps {
    readonly?: boolean;
}

/**
 * What the invoice is and whether it goes out, in the header beside its
 * number and its main action rather than a row of the form each (#2250).
 * A completed invoice shows neither: its kind is the fixed title (FRM-18), and
 * whether it was sent is history.
 */
export const PaloInvoiceHeadOptions = ({ readonly }: PaloInvoiceHeadProps) => {
    const dispatch = useThunkDispatch();
    const formik = useFormikContext<OverviewFormValues>();

    if (readonly) {
        return null;
    }

    return (
        <>
            <PaloDetailSlot name="kind">
                <PaloSegmentedToggle
                    value={formik.values.isCredit ?? false}
                    options={[
                        { value: false, label: <FormattedMessage id="app.invoices.detail.overview.isCredit.false" /> },
                        { value: true, label: <FormattedMessage id="app.invoices.detail.overview.isCredit.true" /> },
                    ]}
                    onChange={value => {
                        formik.setFieldValue(overviewFieldNames.isCredit, value);
                        formik.setFieldTouched(overviewFieldNames.isCredit, true);
                        dispatch(toggleIsCredit());
                    }}
                />
            </PaloDetailSlot>
            <PaloSendOption
                name={overviewFieldNames.shouldSendEmail}
                label={<FormattedMessage id="app.invoices.detail.overview.sendToRecipient" />}
            />
        </>
    );
};

/** Dates and the person in charge — the invoice's own data in the head grid. */
export const PaloInvoiceHeadInvoice = ({ readonly }: PaloInvoiceHeadProps) => {
    const contacts = useAppSelector(state => state.contacts.flat);
    const formik = useFormikContext<OverviewFormValues>();

    return (
        <>
            <PaloField
                label={
                    <>
                        <FormattedMessage id="app.label.invoiceDate" /> *
                    </>
                }
                name={overviewFieldNames.invoiceDate}
                area="invoiceDate"
            >
                <FormikPoolDatePicker
                    name={overviewFieldNames.invoiceDate}
                    setTimeToMidday={true}
                    readOnly={readonly}
                />
            </PaloField>
            <PaloFormikField<PartialContact>
                name={overviewFieldNames.responsibleContact}
                label={<FormattedMessage id="app.common.responsibleContact" />}
                required
                area="responsibleContact"
            >
                {({ field, hasError }) => (
                    <PoolContactAutocompleteList
                        items={contacts}
                        label={<></>}
                        onSelected={value => formik.setFieldValue(field.name, value)}
                        required={false}
                        error={hasError}
                        readOnly={readonly}
                        {...field}
                    />
                )}
            </PaloFormikField>
        </>
    );
};
